import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getDataPlans, buyData, getUserWallet } from "../../api/apiServices";
import BeneficiaryPicker from "../../components/feature/BeneficiaryPicker";
import { useBeneficiaries, saveBeneficiary } from "../../hooks/useBeneficiaries";
import CouponPicker    from "../../components/feature/CouponPicker";
import CashbackToggle      from "../../components/feature/CashbackToggle";
import PaymentConfirmSheet from "../../components/feature/PaymentConfirmSheet";
import MarketingSlides from "../../components/feature/MarketingSlides";
import { useAuth } from "../../context/useAuth";
import { icons } from "../../utils/icons";
import PageLoader from "../../components/PageLoader";
import { PhoneRequiredGate } from "../../components/feature/ProfilePromptBanner";
import { useAnalytics } from "../../hooks/useAnalytics";

const NETWORKS = [
  { id: "MTN",     label: "MTN",     img: icons.mtn },
  { id: "AIRTEL",  label: "Airtel",  img: icons.airtel },
  { id: "GLO",     label: "Glo",     img: icons.glo },
  { id: "9MOBILE", label: "9mobile", img: null, ri: "ri-phone-line", tone: "success" },
];

// "Offers" is now a first-class tab alongside duration tabs
const FILTER_TABS = ["All", "Offers", "Daily", "Weekly", "Monthly", "3 Months", "Long Term"];

function getPlanDuration(name = "") {
  const n = name.toLowerCase();

  // AirtimeNigeria pipe format: "Airtel: 1GB|Social|7days|₦800"
  // Extract the days/period segment from pipes
  const pipeSegments = n.split("|").map(s => s.trim());
  for (const seg of pipeSegments) {
    if (/^1day$/.test(seg) || /^1\s*day$/.test(seg))           return "Daily";
    if (/^\d+\s*days?$/.test(seg)) {
      const days = parseInt(seg);
      if (days <= 3)  return "Daily";
      if (days <= 14) return "Weekly";
      if (days <= 31) return "Monthly";
      if (days <= 90) return "3 Months";
      return "Long Term";
    }
    if (/^\d+\s*months?$/.test(seg)) {
      const months = parseInt(seg);
      return months <= 1 ? "Monthly" : "Long Term";
    }
    if (/^night$/.test(seg)) return "Daily"; // night plans are daily
  }

  // Fallback: scan the whole string (VTPass style)
  if (/\b1\s*day\b|daily|24hr/i.test(n))                       return "Daily";
  if (/\b[2-9]\s*days?\b|week|7\s*days?/i.test(n))             return "Weekly";
  if (/\b(14|30)\s*days?\b|month/i.test(n))                    return "Monthly";
  if (/\b(60|90)\s*days?\b/i.test(n))                          return "3 Months";
  if (/\b(120|180|365)\s*days?\b|\byear\b/i.test(n))           return "Long Term";

  return "Monthly"; // safe default
}

// ── FIXED / NEW: strip a trailing price fragment baked into the plan
// name — e.g. StroWallet's own naming convention is
// "110MB Daily Plan (1 Day) - N100", "2GB + 2 Mins Monthly Plan - N1,500".
// That embedded price is the PROVIDER's raw price, not what the user is
// actually charged (plan.amount, which can include admin markup) — the
// two can straight-up disagree ("- N1,500" in the name vs a real
// sellingPrice of ₦1530), which looks like a bug when both are shown
// together. The real price is already shown separately on the card, so
// this strips the redundant/potentially-wrong one from the label.
function stripEmbeddedPrice(name = "") {
  return name
    .replace(/\s*[-–]\s*[₦N]\s?[\d,]+(\.\d+)?\s*$/i, "")   // "- N100" / "- ₦1,500"
    .replace(/\s*[-–]\s*[\d,]+(\.\d+)?\s*naira\s*$/i, "")   // "- 750 Naira"
    .replace(/\s*\([₦N]\s?[\d,]+(\.\d+)?\)\s*$/i, "")       // "(N1,500)" trailing
    .trim();
}

// Parses "Airtel: 1.5GB|Social|7days|₦500" → { size: "1.5GB", type: "Social", days: "7days" }
// Falls back gracefully for VTPass-style names
function parsePlanMeta(name = "") {
  const cleaned = stripEmbeddedPrice(name);

  const segments = cleaned.split("|").map(s => s.trim());
  if (segments.length >= 3) {
    // Find size (contains GB/MB), type (Social/Direct/Night/CG), duration
    const size     = segments.find(s => /\d+(\.\d+)?\s*(gb|mb)/i.test(s)) || "";
    const duration = segments.find(s => /\d+\s*(day|month|year|hr)/i.test(s)) || "";
    const type     = segments.find(s => /^(social|direct|night|cg|sme|corporate)$/i.test(s)) || "";
    return { size, duration, type, extra: "" };
  }

  // VTPass/StroWallet style — try to extract from natural language
  const sizeMatch = cleaned.match(/(\d+(?:\.\d+)?\s*(?:GB|MB))/i);
  const dayMatch   = cleaned.match(/(\d+\s*(?:days?|months?))/i);

  // NEW: whatever's left after pulling out the size and duration tokens
  // is genuinely useful info that was previously discarded entirely —
  // bonus data ("+ 1GB YouTube Night"), extras ("+ 2mins"), plan tier
  // ("Xtra Bundle") etc. Shown as a description line under the size chip
  // instead of being thrown away.
  let extra = cleaned;
  if (sizeMatch) extra = extra.replace(sizeMatch[0], "");
  if (dayMatch)  extra = extra.replace(dayMatch[0], "");
  extra = extra
    .replace(/\bplan\b/i, "")
    .replace(/^[\s\-–(),.]+|[\s\-–(),.]+$/g, "") // trim stray separators left behind
    .replace(/\s{2,}/g, " ")
    .trim();

  return {
    size:     sizeMatch?.[1] || "",
    duration: dayMatch?.[1]  || "",
    type:     "",
    extra,
  };
}

// Format countdown for offer expiry
function getOfferTimeLeft(offerEndsAt) {
  if (!offerEndsAt) return null;
  const diff = new Date(offerEndsAt) - Date.now();
  if (diff <= 0) return "Expired";
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h > 48) return `${Math.floor(h / 24)}d left`;
  if (h > 0)  return `${h}h ${m}m left`;
  return `${m}m left`;
}

// Plan-type chip color, mapped onto the existing semantic token scale
// (no dedicated indigo token, so "night" folds into info alongside "social")
const TYPE_TONE = {
  social:    "info",
  night:     "info",
  direct:    "muted",
  cg:        "purple",
  sme:       "warning",
};

export default function DataPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userLoading = false, userReady } = useAuth();
  const { mostRecent } = useBeneficiaries("data");

  const { track, conversion } = useAnalytics();

  const [network,      setNetwork]      = useState(searchParams.get("network") || mostRecent?.network || "MTN");
  const [phone,        setPhone]        = useState(mostRecent?.phone || "");
  const [plans,        setPlans]        = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [pinError,     setPinError]     = useState("");
  const [activeTab,    setActiveTab]    = useState("All");
  const [nairaBalance, setNairaBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const pendingNetwork = useRef(network);

  // ── Coupon + cashback state ────────────────────────────────
  const [appliedCoupon,  setAppliedCoupon]  = useState(null);
  const [cashbackToUse,  setCashbackToUse]  = useState(0);

  useEffect(() => {
    track.viewItem("data");
  }, []);

  const grossAmount   = selectedPlan ? Number(selectedPlan.amount) : 0;
  const couponSaving  = appliedCoupon?.discount || 0;
  const totalSavings  = couponSaving + cashbackToUse;
  const netAmount     = Math.max(grossAmount - totalSavings, 0);

  const isValid    = phone.length === 11 && !!selectedPlan;
  const hasBalance = nairaBalance !== null && selectedPlan ? nairaBalance >= netAmount : true;
  const canPay = isValid && hasBalance;

  // Balance
  useEffect(() => {
    getUserWallet()
      .then(res => { if (res.status) setNairaBalance(res.data?.nairaBalance || 0); })
      .catch(() => {})
      .finally(() => setBalanceLoading(false));
  }, []);

  // Plans
  useEffect(() => {
    const thisNetwork = network;
    pendingNetwork.current = thisNetwork;
    setPlans([]);
    setSelectedPlan(null);
    setActiveTab("All");
    setAppliedCoupon(null);
    setCashbackToUse(0);
    setPlansLoading(true);

    getDataPlans(thisNetwork)
      .then(res => {
        if (pendingNetwork.current !== thisNetwork) return;
        if (res.status) {
          setPlans((res.plans || []).map(p => ({ ...p, _network: thisNetwork })));
        } else {
          toast.error("Could not load data plans");
        }
      })
      .catch(() => { if (pendingNetwork.current === thisNetwork) toast.error("Failed to fetch data plans"); })
      .finally(() => { if (pendingNetwork.current === thisNetwork) setPlansLoading(false); });
  }, [network]);

  // Filtered plans — "Offers" tab filters by isOffer flag
  const filteredPlans = useMemo(() => {
    if (activeTab === "All")    return plans;
    if (activeTab === "Offers") return plans.filter(p => p.isOffer);
    return plans.filter(p => getPlanDuration(p.name) === activeTab);
  }, [plans, activeTab]);

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts = { All: plans.length, Offers: plans.filter(p => p.isOffer).length };
    ["Daily", "Weekly", "Monthly"].forEach(t => {
      counts[t] = plans.filter(p => getPlanDuration(p.name) === t).length;
    });
    return counts;
  }, [plans]);

  const handleSelectPlan = (plan) => {
    if (selectedPlan?._network === plan._network && selectedPlan?.variationCode === plan.variationCode) {
      setSelectedPlan(null);
    } else {
      setSelectedPlan(plan);
    }
  };

  const isSelected = (plan) =>
    selectedPlan?._network === plan._network &&
    selectedPlan?.variationCode === plan.variationCode;

  const handlePay = () => {
  if (canPay && !loading) {
    track.beginCheckout("data", grossAmount); // fire before showing sheet
    setPinError("");
    setShowConfirm(true);
  }
};

  const executePay = async (pin) => {
    setShowConfirm(false);
    setLoading(true);
    try {
      const res = await buyData(
        network, phone, selectedPlan.variationCode,
        grossAmount,
        user?.phone,
        appliedCoupon?.couponId || null,
        appliedCoupon?.discount || 0,
        cashbackToUse,
        pin,
      );
      if (res.status) {
        saveBeneficiary({ id: phone, type: "data", phone, network, label: phone, lastUsed: Date.now() });
        track.purchase("data", netAmount, res.transactionId);
        // If API tells you it's first ever bill payment:
        if (res.isFirstBillPayment) {
          conversion.firstPurchase(netAmount, "data");
        }
      navigate("/bills/success", {
          state: {
            type: "data", title: `${network} ${selectedPlan.name}`,
            detail: phone, amount: netAmount,
            grossAmount, couponSaving, cashbackUsed: cashbackToUse,
            couponCode: appliedCoupon?.code || null,
            requestId: res.requestId, 
            txStatus:      res.txStatus || "completed",   // ← ADD THIS
            transactionId: res.transactionId,
          },
        });
      } else {
        toast.error(res.message || "Data purchase failed");
      }
    } catch (err) {
      const errData = err?.response?.data;
      const errCode = errData?.code || "";
      const errMsg  = errData?.message || "";

      if (errCode === "PIN_INVALID" || errCode === "PIN_LOCKED" || errCode === "PIN_REQUIRED") {
        setShowConfirm(true);
        setPinError(errMsg);
        setLoading(false);
        return;
      }

      if (err?.response?.status === 202 || errCode === "PROCESSING") {
        navigate("/bills/success", {
          state: {
            type:     "data",
            title:    "Data Bundle",
            amount:   0,
            txStatus: "processing",
            reference: errData?.reference || null,
          },
        });
        return;
      }

      toast.error(errMsg || "Data Bundle failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading || balanceLoading) return <PageLoader size="md" message="Data plans loading..." />;

  const offerCount = tabCounts["Offers"] || 0;

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/bills")} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div>
              <h1 className="text-primary font-bold text-lg font-display">Buy Data</h1>
              <p className="text-muted text-xs">All networks, all bundles</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-brand-light border border-brand-border px-3 py-1.5 rounded-xl">
            <i className="ri-wallet-3-line text-brand text-xs" />
            <span className="text-brand-text text-xs font-bold">
              {balanceLoading ? "..." : `₦${(nairaBalance || 0).toLocaleString()}`}
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <PhoneRequiredGate user={user} userLoading={userLoading} userReady={userReady}>
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-36 space-y-5">

        {/* Marketing slide */}
        <MarketingSlides page="data" compact />

        {/* Network selector */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Select Network</p>
          <div className="grid grid-cols-4 gap-2">
            {NETWORKS.map(net => (
              <button key={net.id} onClick={() => setNetwork(net.id)}
                className={`flex flex-col items-center gap-2 py-3 rounded-2xl border-2 transition-all ${
                  network === net.id ? "border-brand bg-brand-light" : "border-border bg-surface"
                }`}>
                <div className="w-9 h-9 rounded-xl overflow-hidden flex items-center justify-center bg-surface-muted">
                  {net.img
                    ? <img src={net.img} alt={net.label} className="w-7 h-7 object-contain" />
                    : <i className={`${net.ri} text-lg`} style={{ color: "var(--color-success)" }} />}
                </div>
                <span className="text-xs font-semibold text-secondary">{net.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Phone with beneficiary picker */}
        <div>
          <p className="text-secondary text-sm font-semibold mb-3">Phone Number</p>
          <BeneficiaryPicker
            type="data"
            value={phone}
            onChange={v => { setPhone(v); if (v.length < 11) setSelectedPlan(null); }}
            onSelect={b => { if (b.network) setNetwork(b.network); }}
            placeholder="0801 234 5678"
            numeric
            maxLength={11}
            inputMode="tel"
            suffix={phone.length === 11 ? <i className="ri-checkbox-circle-fill text-brand text-lg" /> : null}
          />
        </div>

        {/* Plans */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-secondary text-sm font-semibold">Choose Plan</p>
            {offerCount > 0 && activeTab !== "Offers" && !plansLoading && (
              <button onClick={() => setActiveTab("Offers")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-warning hover:opacity-90 text-on-brand text-[10px] font-bold transition-colors animate-pulse">
                <i className="ri-fire-line text-xs" />
                {offerCount} offer{offerCount > 1 ? "s" : ""} available
              </button>
            )}
          </div>

          {!plansLoading && plans.length > 0 && (
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1 no-scrollbar">
              {FILTER_TABS.map(tab => {
                if (tabCounts[tab] === 0 && tab !== "All") return null;
                const isOfferTab = tab === "Offers";
                const isActive   = activeTab === tab;
                return (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                      isActive
                        ? isOfferTab ? "bg-warning text-on-brand border-warning" : "bg-brand text-on-brand border-brand"
                        : isOfferTab ? "bg-warning-light text-warning-text border-warning-border hover:border-warning" : "bg-surface text-secondary border-border hover:border-brand-border"
                    }`}>
                    {isOfferTab && <i className="ri-fire-line text-xs" />}
                    {tab}
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      isActive
                        ? "bg-white/20 text-on-brand"
                        : isOfferTab ? "bg-warning-light text-warning-text" : "bg-surface-hover text-muted"
                    }`}>{tabCounts[tab]}</span>
                  </button>
                );
              })}
            </div>
          )}

          {activeTab === "Offers" && !plansLoading && filteredPlans.length > 0 && (
            <div className="flex items-center gap-3 bg-warning-light border border-warning-border rounded-2xl px-4 py-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-warning-light flex items-center justify-center flex-shrink-0"
                style={{ boxShadow: "inset 0 0 0 1px var(--color-warning-ring)" }}>
                <i className="ri-fire-fill text-warning text-lg" />
              </div>
              <div>
                <p className="text-warning-text text-xs font-bold">Limited-Time Offers</p>
                <p className="text-warning-text text-[10px] opacity-80">Discounted plans — grab them before they expire</p>
              </div>
            </div>
          )}

          {plansLoading ? (
            <div className="grid grid-cols-2 gap-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 bg-surface rounded-2xl border border-border animate-pulse" />
              ))}
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="bg-surface rounded-2xl border border-border p-8 text-center">
              <i className="ri-wifi-off-line text-4xl text-faint mb-2 block" />
              <p className="text-muted text-sm">
                {plans.length === 0
                  ? `No plans available for ${network}`
                  : activeTab === "Offers" ? `No active offers for ${network} right now`
                  : `No ${activeTab.toLowerCase()} plans for ${network}`}
              </p>
              {plans.length > 0 && (
                <button onClick={() => setActiveTab("All")} className="mt-2 text-brand text-xs font-semibold">Show all plans</button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {filteredPlans.map(plan => {
  const selected  = isSelected(plan);
  const timeLeft  = plan.isOffer ? getOfferTimeLeft(plan.offerEndsAt) : null;
  const isExpired = timeLeft === "Expired";
  const meta      = parsePlanMeta(plan.name);
  const duration  = getPlanDuration(plan.name);

  const typeKey   = meta.type.toLowerCase();
  const typeTone  = TYPE_TONE[typeKey] || "muted";
  const typeStyle = typeTone === "muted"
    ? { background: "var(--color-surface-hover)", color: "var(--color-text-muted)" }
    : { background: `var(--color-${typeTone}-light)`, color: `var(--color-${typeTone}-text)` };

  return (
    <button key={`${plan._network}-${plan.variationCode}`}
      onClick={() => !isExpired && handleSelectPlan(plan)}
      disabled={isExpired}
      className={`p-3.5 rounded-2xl border-2 text-left transition-all relative overflow-hidden flex flex-col gap-2 shadow-card ${
        selected
          ? "border-brand bg-brand-light"
          : plan.isOffer && !isExpired
          ? "border-warning-border bg-warning-light hover:border-warning"
          : "border-border bg-surface hover:border-border-strong"
      } ${isExpired ? "opacity-50 cursor-not-allowed" : "active:scale-95"}`}>

      {/* Offer badge */}
      {plan.isOffer && (
        <div className={`absolute top-0 right-0 px-2 py-0.5 text-[9px] font-bold rounded-bl-xl ${
          isExpired ? "bg-surface-hover text-muted" : "bg-warning text-on-brand"
        }`}>{isExpired ? "Expired" : "OFFER"}</div>
      )}

      {/* Size — big and prominent */}
      {meta.size ? (
        <div>
          <p className="font-black text-primary text-xl leading-none font-display">
            {meta.size}
          </p>
          {meta.type && (
            <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-md mt-1" style={typeStyle}>
              {meta.type.toUpperCase()}
            </span>
          )}
          {/* NEW: bonus/extra details that used to be silently dropped
              once a size was matched — e.g. "+ 2mins", "1GB YouTube
              Night", "Xtra Bundle". Price fragment already stripped by
              parsePlanMeta/stripEmbeddedPrice before this reaches here. */}
          {meta.extra && (
            <p className="text-muted text-[10px] leading-snug mt-1 line-clamp-2">
              {meta.extra}
            </p>
          )}
        </div>
      ) : (
        // Fallback — no size pattern matched at all: show the cleaned
        // (price-stripped) name so at least nothing misleading shows,
        // instead of the raw name which might still carry "- N1,500".
        <p className="font-bold text-primary text-sm leading-tight pr-6 font-display">
          {stripEmbeddedPrice(plan.name)}
        </p>
      )}

      {/* Price */}
      <p className="font-bold text-base" style={{
        color: selected ? "var(--color-brand-text)" : plan.isOffer && !isExpired ? "var(--color-warning-text)" : "var(--color-brand)"
      }}>
        ₦{Number(plan.amount).toLocaleString()}
        {plan.isOffer && plan.providerPrice && plan.providerPrice !== plan.amount && (
          <span className="text-[10px] text-faint line-through ml-1.5">
            ₦{Number(plan.providerPrice).toLocaleString()}
          </span>
        )}
      </p>

      {/* Duration + countdown */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
          selected ? "bg-brand-light text-brand-text" : "bg-surface-hover text-muted"
        }`}>
          {meta.duration || duration}
        </span>
        {plan.isOffer && timeLeft && !isExpired && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-warning-light text-warning-text flex items-center gap-0.5">
            <i className="ri-time-line text-[8px]" />{timeLeft}
          </span>
        )}
      </div>

      {/* Selected checkmark */}
      {selected && (
        <div className="absolute top-2 right-2">
          <i className="ri-checkbox-circle-fill text-brand text-base" />
        </div>
      )}
    </button>
  );
})}
            </div>
          )}
        </div>

        {/* Cashback toggle */}
        {selectedPlan && (
          <CashbackToggle
            purchaseAmount={grossAmount}
            appliedAmount={cashbackToUse}
            onApply={setCashbackToUse}
          />
        )}

        {/* Coupon picker */}
        {selectedPlan && (
          <div>
            <p className="text-secondary text-sm font-semibold mb-3">Coupon / Discount</p>
            <CouponPicker
              service="data"
              amount={grossAmount}
              appliedCoupon={appliedCoupon}
              onApply={setAppliedCoupon}
              onRemove={() => setAppliedCoupon(null)}
            />
          </div>
        )}

        {/* Order summary */}
        {(appliedCoupon || cashbackToUse > 0) && selectedPlan && (
          <div className="bg-brand-light border border-brand-border rounded-2xl px-4 py-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted">{selectedPlan.name}</span>
              <span className="text-primary font-semibold">₦{grossAmount.toLocaleString()}</span>
            </div>
            {cashbackToUse > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-brand-text font-semibold flex items-center gap-1">
                  <i className="ri-hand-coin-line text-xs" />Cashback
                </span>
                <span className="text-brand-text font-bold">−₦{cashbackToUse.toLocaleString()}</span>
              </div>
            )}
            {appliedCoupon && (
              <div className="flex justify-between text-sm">
                <span className="text-brand-text font-semibold flex items-center gap-1">
                  <i className="ri-coupon-3-line text-xs" />{appliedCoupon.code}
                </span>
                <span className="text-brand-text font-bold">−₦{couponSaving.toLocaleString()}</span>
              </div>
            )}
            <div className="h-px bg-brand-border" />
            <div className="flex justify-between text-sm">
              <span className="text-primary font-bold">You pay</span>
              <span className="text-brand-text font-bold text-base">₦{netAmount.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>
      </PhoneRequiredGate>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border px-5 py-4 space-y-2">
        {selectedPlan && !hasBalance && nairaBalance !== null && (
          <div className="flex items-center gap-2 px-3 py-2 bg-danger-light border border-danger-border rounded-xl">
            <i className="ri-error-warning-line text-danger text-sm flex-shrink-0" />
            <p className="text-danger-text text-xs font-medium flex-1">
              Insufficient balance — need ₦{Number(selectedPlan.amount).toLocaleString()}.{" "}
              <button onClick={() => navigate("/deposit")} className="underline font-semibold">Top up</button>
            </p>
          </div>
        )}
        {selectedPlan && (
          <div className={`flex items-center justify-between px-3 py-2 rounded-xl border ${
            selectedPlan.isOffer ? "bg-warning-light border-warning-border" : "bg-brand-light border-brand-border"
          }`}>
            <div className="flex items-center gap-1.5">
              {selectedPlan.isOffer && <i className="ri-fire-line text-warning text-xs" />}
              <span className="text-xs font-semibold" style={{ color: selectedPlan.isOffer ? "var(--color-warning-text)" : "var(--color-brand-text)" }}>
                {stripEmbeddedPrice(selectedPlan.name)}
              </span>
            </div>
            <div className="text-right">
              {totalSavings > 0 && <p className="text-brand text-[10px] line-through">₦{grossAmount.toLocaleString()}</p>}
              <span className="text-xs font-bold" style={{ color: selectedPlan.isOffer ? "var(--color-warning-text)" : "var(--color-brand-text)" }}>
                ₦{(totalSavings > 0 ? netAmount : grossAmount).toLocaleString()}
              </span>
            </div>
          </div>
        )}
        <button onClick={handlePay} disabled={!canPay || loading}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2">
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
          ) : !isValid ? "Select a plan to continue"
            : !hasBalance ? "Insufficient balance"
            : totalSavings > 0
              ? `Pay ₦${netAmount.toLocaleString()} (saved ₦${totalSavings.toLocaleString()})`
              : `Pay ₦${grossAmount.toLocaleString()}`}
        </button>
      </div>
      <PaymentConfirmSheet
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={executePay}
        loading={loading}
        pinError={pinError}
        amount={grossAmount}
        netAmount={netAmount}
        couponSaving={couponSaving}
        cashbackUsed={cashbackToUse}
        rows={[
          { label: "Network",   value: network },
          { label: "Phone",     value: phone, mono: true },
          { label: "Plan",      value: selectedPlan ? stripEmbeddedPrice(selectedPlan.name) : "" },
        ]}
        confirmLabel="Buy Data"
      />
    </div>
  );
}